/**
 * usePageCapabilities — data for the "On this page" section of the assistant
 * panel: the current page's registered capabilities and whether each is
 * implemented or planned. Same registry the agent's capabilities tool reads.
 */

import { useQuery } from '@tanstack/react-query';
import type { AssistantPageId } from '@chatsdet/types';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

export type PageCapability = {
  id: string;
  title: string;
  description: string;
  status: 'implemented' | 'planned';
  global: boolean;
};

export type PageCapabilitiesData = {
  page: { id: string; title: string; description: string } | null;
  capabilities: PageCapability[];
};

async function fetchPageCapabilities(pageId: AssistantPageId): Promise<PageCapabilitiesData> {
  const res = await fetch(`${API_URL}/assistant/page-capabilities?pageId=${encodeURIComponent(pageId)}`, {
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res, 'Failed to load page capabilities');
  const json = (await res.json()) as { ok: boolean; data: PageCapabilitiesData };
  return json.data;
}

export function usePageCapabilities(pageId: AssistantPageId, enabled: boolean) {
  return useQuery({
    queryKey: ['assistant', 'page-capabilities', pageId],
    queryFn: () => fetchPageCapabilities(pageId),
    enabled: enabled && pageId !== 'unknown',
    staleTime: 5 * 60_000,
  });
}
